import { useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Node } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from "@tiptap/extension-text-style";
import ImageResize from "tiptap-extension-resize-image";

const IMAGE_MAX = 10 * 1024 * 1024;
const VIDEO_MAX = 100 * 1024 * 1024;
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const VIDEO_TYPES = ["video/mp4", "video/webm"];

const FONT_SIZES = ["12px", "14px", "16px", "18px", "22px", "28px"];

// Nó mínimo de vídeo (uploads da wiki) — o StarterKit não tem vídeo.
const Video = Node.create({
  name: "video",
  group: "block",
  atom: true,
  addAttributes() {
    return { src: { default: null } };
  },
  parseHTML() {
    return [{ tag: "video" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["video", { controls: "true", preload: "metadata", ...HTMLAttributes }];
  },
});

async function uploadMedia(file: File): Promise<string> {
  const resp = await fetch("/api/wiki/media", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!resp.ok) {
    let detail = "Falha no upload";
    try { detail = (await resp.json()).detail ?? detail; } catch {}
    throw new Error(detail);
  }
  return (await resp.json()).url;
}

const tbBtn = (active: boolean): React.CSSProperties => ({
  padding: "4px 9px", fontSize: 13, cursor: "pointer", borderRadius: 6,
  background: active ? "var(--brand-soft)" : "transparent",
  color: active ? "var(--brand)" : "var(--ink-secondary)",
  border: active ? "1px solid var(--brand)" : "1px solid transparent",
  lineHeight: 1.4,
});

const tbSelect: React.CSSProperties = {
  padding: "4px 6px", fontSize: 12, borderRadius: 6, cursor: "pointer",
  background: "var(--surface-input)", color: "var(--ink)", border: "1px solid var(--line)",
};

export function RichEditor({
  html,
  onChange,
}: {
  html: string;
  onChange: (html: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, TextStyleKit, ImageResize, Video],
    content: html,
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { class: "wiki-md wiki-editor-content" },
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []);
        if (files.length) { handleFiles(files); return true; }
        return false;
      },
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (files.length) { event.preventDefault(); handleFiles(files); return true; }
        return false;
      },
    },
  });

  async function handleFiles(files: File[]) {
    if (!editor) return;
    setUploadError(null);
    for (const file of files) {
      const isImage = IMAGE_TYPES.includes(file.type);
      const isVideo = VIDEO_TYPES.includes(file.type);
      if (!isImage && !isVideo) {
        setUploadError(`"${file.name}": tipo não suportado (imagens png/jpg/webp/gif, vídeos mp4/webm)`);
        continue;
      }
      if (file.size > (isImage ? IMAGE_MAX : VIDEO_MAX)) {
        setUploadError(`"${file.name}": excede o limite de ${isImage ? 10 : 100} MB`);
        continue;
      }
      setUploading(true);
      try {
        const url = await uploadMedia(file);
        if (isImage) editor.chain().focus().setImage({ src: url }).run();
        else editor.chain().focus().insertContent({ type: "video", attrs: { src: url } }).run();
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : "Falha no upload");
      } finally {
        setUploading(false);
      }
    }
  }

  if (!editor) return null;

  const currentSize = (editor.getAttributes("textStyle").fontSize as string | undefined) ?? "";
  const currentColor = (editor.getAttributes("textStyle").color as string | undefined) ?? "#1a1a1a";
  const blockValue = editor.isActive("heading", { level: 1 }) ? "h1"
    : editor.isActive("heading", { level: 2 }) ? "h2"
    : editor.isActive("heading", { level: 3 }) ? "h3"
    : "p";

  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 8, background: "var(--surface-input)" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", padding: "8px 10px", borderBottom: "1px solid var(--line)" }}>
        <select
          value={blockValue}
          onChange={e => {
            const v = e.target.value;
            const c = editor.chain().focus();
            if (v === "p") c.setParagraph().run();
            else c.toggleHeading({ level: Number(v[1]) as 1 | 2 | 3 }).run();
          }}
          style={tbSelect}
        >
          <option value="p">Parágrafo</option>
          <option value="h1">Título 1</option>
          <option value="h2">Título 2</option>
          <option value="h3">Título 3</option>
        </select>

        <select
          value={FONT_SIZES.includes(currentSize) ? currentSize : ""}
          onChange={e => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontSize(v).run();
            else editor.chain().focus().unsetFontSize().run();
          }}
          style={tbSelect}
          title="Tamanho da fonte"
        >
          <option value="">Fonte</option>
          {FONT_SIZES.map(s => <option key={s} value={s}>{s.replace("px", "")}</option>)}
        </select>

        <label title="Cor do texto" style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", padding: "2px 4px" }}>
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(currentColor) ? currentColor : "#1a1a1a"}
            onChange={e => editor.chain().focus().setColor(e.target.value).run()}
            style={{ width: 24, height: 22, padding: 0, border: "1px solid var(--line)", borderRadius: 4, background: "none", cursor: "pointer" }}
          />
        </label>

        <span style={{ width: 1, height: 18, background: "var(--line)", margin: "0 4px" }} />

        <button onClick={() => editor.chain().focus().toggleBold().run()} style={{ ...tbBtn(editor.isActive("bold")), fontWeight: 700 }} title="Negrito">B</button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} style={{ ...tbBtn(editor.isActive("italic")), fontStyle: "italic" }} title="Itálico">I</button>
        <button onClick={() => editor.chain().focus().toggleStrike().run()} style={{ ...tbBtn(editor.isActive("strike")), textDecoration: "line-through" }} title="Tachado">S</button>
        <button onClick={() => editor.chain().focus().toggleCode().run()} style={{ ...tbBtn(editor.isActive("code")), fontFamily: "monospace" }} title="Código inline">{"<>"}</button>

        <span style={{ width: 1, height: 18, background: "var(--line)", margin: "0 4px" }} />

        <button onClick={() => editor.chain().focus().toggleBulletList().run()} style={tbBtn(editor.isActive("bulletList"))} title="Lista">• —</button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} style={tbBtn(editor.isActive("orderedList"))} title="Lista numerada">1.</button>
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()} style={tbBtn(editor.isActive("blockquote"))} title="Citação">❝</button>
        <button onClick={() => editor.chain().focus().toggleCodeBlock().run()} style={{ ...tbBtn(editor.isActive("codeBlock")), fontFamily: "monospace" }} title="Bloco de código">{"{ }"}</button>

        <span style={{ width: 1, height: 18, background: "var(--line)", margin: "0 4px" }} />

        <button onClick={() => fileRef.current?.click()} style={tbBtn(false)} title="Inserir imagem ou vídeo">🖼</button>
        <input
          ref={fileRef}
          type="file"
          accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(",")}
          multiple
          style={{ display: "none" }}
          onChange={e => {
            if (e.target.files?.length) handleFiles(Array.from(e.target.files));
            e.target.value = "";
          }}
        />

        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-dim)" }}>
          {uploading ? "Enviando arquivo..." : "Cole ou arraste imagens/vídeos · arraste a borda da imagem pra redimensionar"}
        </span>
      </div>

      <EditorContent editor={editor} />

      {uploadError && (
        <div style={{ padding: "6px 12px", fontSize: 12, color: "var(--danger)", borderTop: "1px solid var(--line)" }}>{uploadError}</div>
      )}
    </div>
  );
}
